import { DSL_API_PATH } from '../../../../constants';
import { getAemVehicleModelData } from '../../../../hooks/use-aem-vehicle-model-data';
import ServiceHandler from '../../../../services/service-handler';
import { SlackMessages } from '../../../../services/slack-message-service/SlackMessageService';
import { useContext, useEffect, useState } from 'react';
import ownerManualService, {
    OwnerManualResponse,
} from '../../../services/owner-manual-service/owner-manual-service';
import VehicleTypeService from '../../../../services/vehicle-type-service/vehicle-type-service';
import ServerContext from '../../../../contexts/serverContext';
import { VehicleAttributes } from '../../../../models/vehicle-attributes';
import { useAnalytics } from '../../../../hooks/use-analytics';
import { scrollToTop } from '../../../../components/sections/vehicle-health/vha-utils';

export const useOwnerManualData = (
    setManualsLoading: (loading: boolean) => void,
    year?: string,
    model?: string,
    vin?: string,
    languageRegionCode?: string
): OwnerManualResponse | null => {
    const doneLoading = () => {
        setManualsLoading(false);
        scrollToTop();
    };
    const [fireEvents] = useAnalytics();
    const {
        currentCountryCode,
        brand,
    } = ServiceHandler.AppConfigurationService;
    const [
        ownerManualsData,
        setOwnerManualsData,
    ] = useState<OwnerManualResponse | null>(null);
    const { currentLanguageRegionCode, current3LetterCountryCode } = useContext(
        ServerContext
    );
    const fireOwnerManualDetailsAnalytics = (year: number, model: string) => {
        const vehicleAttributes: VehicleAttributes = {
            year: year,
            model: model,
            make: brand,
        };
        fireEvents(
            ['owner-owner-manuals-details'],
            undefined,
            {
                ownerManuals: 'owner-manuals-details',
                ymm: vehicleAttributes,
            },
            false
        );
    };
    const callOwnerManualsApi = (
        vin: string,
        modelYear?: number,
        modelName?: string
    ) => {
        ownerManualService
            .getOwnerManualByVin(vin, modelName, languageRegionCode)
            .then(omResponse => {
                omResponse.metadata.displayModelName = modelName || '';
                omResponse.metadata.modelYear = `${modelYear || ''}`;
                omResponse.metadata.seoName = '';
                setOwnerManualsData(omResponse);
                modelYear &&
                    modelName &&
                    fireOwnerManualDetailsAnalytics(modelYear, modelName);
                doneLoading();
            })
            .catch(e => {
                setOwnerManualsData({
                    metadata: {
                        displayModelName: '',
                        modelYear: '',
                        seoName: '',
                        error: new Error(
                            `owner-manual ${SlackMessages.API_RETURNS_ERROR}${vin}: ${e.message} with api ${DSL_API_PATH.OWNERS_MANUAL_VIN_V3}`
                        ),
                    },
                });
                doneLoading();
            });
    };
    useEffect(() => {
        setManualsLoading(true);
        if (
            (currentLanguageRegionCode && languageRegionCode !== 'CA-ES') ||
            (currentLanguageRegionCode === 'es-es' &&
                languageRegionCode === 'CA-ES')
        ) {
            if (year && model) {
                getAemVehicleModelData(
                    brand,
                    currentCountryCode,
                    currentLanguageRegionCode,
                    year,
                    model,
                    'SEO_KEY'
                ).then(response => {
                    const displayModelName: string = response?.model || model;
                    ownerManualService
                        .getOwnerManualByModelYear(
                            response?.marketName || displayModelName,
                            year,
                            languageRegionCode
                        )
                        .then(omResponse => {
                            omResponse.metadata.displayModelName = displayModelName;
                            omResponse.metadata.modelYear = year;
                            omResponse.metadata.seoName = response?.seoKey;

                            setOwnerManualsData(omResponse);

                            fireOwnerManualDetailsAnalytics(
                                parseInt(year),
                                displayModelName
                            );
                            doneLoading();
                        })
                        .catch(e => {
                            setOwnerManualsData({
                                metadata: {
                                    displayModelName: '',
                                    modelYear: '',
                                    seoName: '',
                                    error: new Error(
                                        `owner-manual ${SlackMessages.API_YEAR_MODEL_ERROR}${year} ${displayModelName}: ${e.message} with api ${DSL_API_PATH.OWNER_MANUALS}`
                                    ),
                                },
                            });
                            doneLoading();
                        });
                });
            } else if (vin) {
                new VehicleTypeService()
                    .request(
                        vin,
                        currentLanguageRegionCode,
                        current3LetterCountryCode?.toUpperCase()
                    )
                    .then(response => {
                        callOwnerManualsApi(
                            vin,
                            Number(response.vehicleData?.year),
                            response.vehicleData?.model
                        );
                    })
                    .catch(e => {
                        console.error(
                            `owner-manual ${SlackMessages.VIN_API_FAILURE}${vin}: ${e.message} with api ${DSL_API_PATH.VEHICLE_TYPE}`
                        );
                        callOwnerManualsApi(vin);
                    });
            }
        }
    }, [model, year, vin]);
    return ownerManualsData;
};
